import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { getUser } from "../lib/auth";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { ConfirmDialog, AlertDialog } from "../components/ConfirmDialog";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { useSortable } from "../lib/useSortable";
import { usePersistedPage } from "../lib/usePersistedPage";

type SubFile = { name: string; url: string; size: number; type: string };

type Submission = {
  id: number;
  title: string;
  note: string | null;
  files: SubFile[];
  status: "received" | "processing" | "done" | "rejected";
  reject_reason: string | null;
  created_at: string;
  user_id: number;
  user_email: string;
  user_name: string;
  user_company: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  received: "待处理",
  processing: "制作中",
  done: "已交付",
  rejected: "被驳回",
};
const STATUS_COLOR: Record<string, string> = {
  received: "#888",
  processing: "#b8860b",
  done: "#2e8b57",
  rejected: "#b04a3a",
};

export function StudioSubmissionsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Submission[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = usePersistedPage("kexi_studio_subs_page");
  const [delFor, setDelFor] = useState<Submission | null>(null);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);
  const isAdmin = getUser()?.role === "admin";
  const { containerRef, pageSize } = useAutoPageSize();

  useEffect(() => {
    api.get<Submission[]>("/api/admin/studio/submissions").then(setRows).catch((e) => setError(String(e)));
  }, []);

  const doDelete = async () => {
    if (!delFor) return;
    const target = delFor;
    setDelFor(null);
    try {
      await api.delete(`/api/admin/studio/submissions/${target.id}`);
      setRows((rs) => rs?.filter((x) => x.id !== target.id) ?? null);
    } catch (e) {
      // 已生成关联报告 → 后端返回 409
      if (e instanceof ApiError && e.status === 409) setAlertMsg("该提交已生成关联报告，请先在「智库报告」中删除对应报告后再删除此提交。");
      else setError(String(e));
    }
  };

  const { sorted, sortKey, sortDir, toggle } = useSortable((rows ?? []) as unknown as Record<string, unknown>[]);
  const pageRows = rows ? (sorted as unknown as Submission[]).slice((page - 1) * pageSize, page * pageSize) : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">智库提交</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            注册用户上传的调研资料；点击行进入明细，可查看原始资料、驳回或挂载报告
          </div>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {!rows && !error && <div className="muted">加载中…</div>}
      {rows && rows.length === 0 && <div className="muted">暂无提交</div>}

      <div ref={containerRef}>
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
              <SortableTh sortKey="created_at" {...s} style={{ width: 100 }}>时间</SortableTh>
              <SortableTh sortKey="user_name" {...s} style={{ width: 120 }}>用户</SortableTh>
              <SortableTh sortKey="user_company" {...s} style={{ width: 140 }}>公司</SortableTh>
              <th style={{ width: 60 }}>文件</th>
              <SortableTh sortKey="title" {...s}>标题</SortableTh>
              <SortableTh sortKey="status" {...s} style={{ width: 90 }}>状态</SortableTh>
              {isAdmin && <th style={{ width: 70 }}></th>}
              </>; })()}
            </tr>
          </thead>
          <tbody>
            {pageRows!.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => nav(`/studio/submissions/${r.id}`)}>
                <td className="muted">{new Date(r.created_at).toLocaleDateString("zh-CN")}</td>
                <td>
                  {r.user_name}
                  <div className="muted" style={{ fontSize: 11 }}>{r.user_email}</div>
                </td>
                <td>{r.user_company || <span className="muted">—</span>}</td>
                <td>{r.files.length}</td>
                <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.title || <span className="muted">—</span>}
                </td>
                <td><span style={{ fontSize: 12, color: STATUS_COLOR[r.status] }}>{STATUS_LABEL[r.status]}</span></td>
                {isAdmin && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-xs" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => setDelFor(r)}>删除</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows && <Pager page={page} pageSize={pageSize} total={rows.length} onChange={setPage} />}
      </div>

      <ConfirmDialog
        open={delFor !== null}
        title="删除智库提交"
        message={delFor ? <>确认删除「{delFor.user_name}」的提交「{delFor.title || "无标题"}」？提交、往来记录及其附件文件将被永久删除，不可恢复。</> : null}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setDelFor(null)}
      />
      <AlertDialog open={alertMsg !== null} title="无法删除" message={alertMsg} danger onClose={() => setAlertMsg(null)} />
    </div>
  );
}
